<style scoped>

.dashboard_nat_maps {
    padding: 24px;
}

.dashboard_nat_maps_controls {
    align-items: center;
    display: flex;
    flex-wrap: wrap;
    gap: 12px;
    margin-bottom: 16px;
}

.dashboard_nat_maps_select {
    flex: 0 1 300px;
    min-width: 230px;
}

.dashboard_nat_maps_ranges {
    align-items: center;
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
}

.dashboard_nat_maps_chart_container {
    min-height: 580px;
    position: relative;
}

.dashboard_nat_maps_chart {
    height: 580px;
    width: 100%;
}

.dashboard_nat_maps_session_chart_container {
    min-height: 360px;
}

.dashboard_nat_maps_session_chart {
    height: 360px;
    width: 100%;
}

.dashboard_nat_maps_no_data {
    color: rgba(0, 0, 0, 0.6);
    left: 0;
    position: absolute;
    right: 0;
    text-align: center;
    top: 220px;
}

.br {
    color: rgba(109, 103, 164);
}

</style>

<template>
<div class="dashboard_nat_maps">
    <h1 class="br mb-4">NAT maps</h1>

    <div class="dashboard_nat_maps_controls">
        <v-select
            v-model="selected_map_key"
            class="dashboard_nat_maps_select"
            :items="map_options"
            item-text="label"
            item-value="value"
            label="NAT map"
            dense
            outlined
            hide-details
            :disabled="maps_loading || map_options.length === 0"
            :loading="maps_loading"
            @change="change_map"
        ></v-select>
        <v-select
            v-model="selected_resolution"
            class="dashboard_nat_maps_select"
            :items="data_intervals"
            item-text="label"
            item-value="value"
            label="Data interval"
            dense
            outlined
            hide-details
            @change="change_resolution"
        ></v-select>
        <div class="dashboard_nat_maps_ranges">
            <v-btn-toggle
                v-model="selected_range_ms"
                mandatory
                dense
                @change="change_range"
            >
                <v-btn
                    v-for="range in available_ranges"
                    :key="range.label"
                    :value="range.value"
                    small
                >
                    {{ range.label }}
                </v-btn>
            </v-btn-toggle>
            <v-btn
                class="ml-2"
                small
                :disabled="loading || selected_map === null"
                @click="load_metrics"
            >
                Refresh
            </v-btn>
        </div>
    </div>

    <v-card outlined>
        <v-card-title>{{ selected_map_title }}</v-card-title>
        <v-card-subtitle v-if="host && selected_map">
            Host {{ host }}, BR instance {{ instance }},
            {{ selected_map.label }}
        </v-card-subtitle>
        <v-progress-linear
            v-if="loading || maps_loading"
            indeterminate
            color="primary"
        ></v-progress-linear>
        <v-alert
            v-if="error_message"
            class="ma-4"
            type="error"
            outlined
        >
            {{ error_message }}
        </v-alert>
        <v-card-text>
            <div class="dashboard_nat_maps_chart_container">
                <div ref="metrics_chart" class="dashboard_nat_maps_chart"></div>
                <div
                    v-if="!loading && !maps_loading &&
                        !error_message && points_count === 0"
                    class="dashboard_nat_maps_no_data"
                >
                    {{ no_data_message }}
                </div>
            </div>
        </v-card-text>
    </v-card>

    <v-card class="mt-4" outlined>
        <v-card-title>
            {{ selected_map ? selected_map.label : "NAT map" }} session creation and deletion
        </v-card-title>
        <v-progress-linear
            v-if="loading || maps_loading"
            indeterminate
            color="primary"
        ></v-progress-linear>
        <v-alert
            v-if="session_error_message"
            class="ma-4"
            type="error"
            outlined
        >
            {{ session_error_message }}
        </v-alert>
        <v-card-text>
            <div class="dashboard_nat_maps_chart_container dashboard_nat_maps_session_chart_container">
                <div ref="session_chart" class="dashboard_nat_maps_session_chart"></div>
                <div
                    v-if="!loading && !maps_loading && !error_message &&
                        !session_error_message && session_points_count === 0"
                    class="dashboard_nat_maps_no_data"
                >
                    No session-rate samples are available for this map and time range.
                    Collection needs two samples; downsampled data appears after aggregation.
                </div>
            </div>
        </v-card-text>
    </v-card>
</div>
</template>

<script>

import * as echarts from "echarts";
import api_client from "../services/api.js";


const HOUR_MS = 60 * 60 * 1000;
const DAY_MS = 24 * HOUR_MS;

const NAT_MAP_TYPES = ["snat", "det snat", "nat64"];

const METRIC_SERIES = [
    { name: "Total", field: "active_connections" },
    { name: "Unknown", field: "unknown" },
    { name: "UDP active", field: "active" },
    { name: "TCP SYN sent", field: "syn_sent" },
    { name: "TCP established", field: "established" },
    { name: "TCP FIN wait", field: "fin_wait" },
    { name: "TCP close wait", field: "close_wait" },
    { name: "TCP closing", field: "closing" },
    { name: "TCP last ACK", field: "last_ack" },
    { name: "TCP closed", field: "closed" },
    { name: "ICMP active", field: "icmp_active" },
    { name: "DNS", field: "dns" },
    { name: "GRE", field: "gre" },
];

const SESSION_RATE_SERIES = [
    { name: "Created sessions/s", field: "sessions_created_ps" },
    { name: "Deleted sessions/s", field: "sessions_deleted_ps" },
];

const SERIES_COLORS = [
    "#111111",
    "#7f7f7f",
    "#2878b5",
    "#f28e2b",
    "#2a9d55",
    "#d62728",
    "#9467bd",
    "#8c564b",
    "#e377c2",
    "#bcbd22",
    "#17becf",
    "#ff9da7",
    "#4e79a7",
];

const DATA_INTERVALS = [
    {
        label: "1 minute — last 2 weeks",
        value: "1m",
        default_range_ms: DAY_MS,
        ranges: [
            { label: "1 hour", value: HOUR_MS },
            { label: "24 hours", value: DAY_MS },
            { label: "7 days", value: 7 * DAY_MS },
            { label: "14 days", value: 14 * DAY_MS },
        ],
    },
    {
        label: "5 minutes — last 30 days",
        value: "5m",
        default_range_ms: 30 * DAY_MS,
        ranges: [
            { label: "24 hours", value: DAY_MS },
            { label: "7 days", value: 7 * DAY_MS },
            { label: "14 days", value: 14 * DAY_MS },
            { label: "30 days", value: 30 * DAY_MS },
        ],
    },
    {
        label: "1 hour — last year",
        value: "1h",
        default_range_ms: 365 * DAY_MS,
        ranges: [
            { label: "7 days", value: 7 * DAY_MS },
            { label: "30 days", value: 30 * DAY_MS },
            { label: "90 days", value: 90 * DAY_MS },
            { label: "1 year", value: 365 * DAY_MS },
        ],
    },
];


function format_number(value) {
    return value.toFixed(0);
}


function count_unit(value) {
    const absolute_value = Math.abs(value);
    if (absolute_value >= 1e9) {
        return { divisor: 1e9, prefix: "G" };
    }
    if (absolute_value >= 1e6) {
        return { divisor: 1e6, prefix: "M" };
    }
    if (absolute_value >= 1e3) {
        return { divisor: 1e3, prefix: "K" };
    }
    return { divisor: 1, prefix: "" };
}


function format_axis_count(value) {
    const unit = count_unit(value);
    return `${format_number(value / unit.divisor)} ${unit.prefix}`.trim();
}


function format_tooltip_count(value) {
    if (value === null || value === undefined || !Number.isFinite(value)) {
        return "-";
    }
    const unit = count_unit(value);
    return `${format_number(value / unit.divisor)} ${unit.prefix}`.trim();
}


function format_session_rate(value) {
    if (!Number.isFinite(value)) {
        return "-";
    }
    const unit = count_unit(value);
    const count = (value / unit.divisor).toLocaleString(undefined, {
        maximumSignificantDigits: 3,
    });
    return `${count} ${unit.prefix}`.trim();
}


function format_tooltip_session_rate(value) {
    return Number.isFinite(value) ? `${format_session_rate(value)} sessions/s` : "-";
}


function parse_map_response(data) {
    let value = data;
    if (typeof value === "string") {
        value = JSON.parse(value);
    }
    if (value && value.err_msg) {
        throw new Error(value.err_msg);
    }
    if (!Array.isArray(value)) {
        throw new Error("NAT map list is not a JSON array");
    }
    return value;
}


function map_label(map) {
    if (map.map_type === "snat") {
        return `SNAT map ${map.map_id}`;
    }
    if (map.map_type === "det snat") {
        return `DET SNAT map ${map.map_id}`;
    }
    return `NAT64 map ${map.map_id}`;
}


function request_error_message(error) {
    const response = error.response;
    if (response && response.data) {
        return response.data.detail || "Dashboard request failed";
    }
    return error.message || "Dashboard request failed";
}


export default {
    name: "dashboard_nat_maps",

    data: () => ({
        data_intervals: DATA_INTERVALS,
        error_message: "",
        host: "",
        instance: 1,
        loading: false,
        map_options: [],
        maps_loading: false,
        points_count: 0,
        session_points_count: 0,
        session_error_message: "",
        request_id: 0,
        selected_map_key: null,
        selected_range_ms: DAY_MS,
        selected_resolution: "1m",
        zoom_end: 100,
        zoom_start: 0,
    }),

    computed: {
        available_ranges() {
            return this.selected_interval.ranges;
        },

        no_data_message() {
            if (this.map_options.length === 0) {
                return "No SNAT, deterministic SNAT, or NAT64 maps are " +
                    "configured.";
            }
            return "No metrics are available for this NAT map and time range.";
        },

        selected_interval() {
            return this.data_intervals.find(interval => (
                interval.value === this.selected_resolution
            )) || this.data_intervals[0];
        },

        selected_map() {
            return this.map_options.find(map => (
                map.value === this.selected_map_key
            )) || null;
        },

        selected_map_title() {
            return this.selected_map
                ? `${this.selected_map.label} states`
                : "NAT map states";
        },
    },

    created() {
        this.metrics_chart = null;
        this.session_chart = null;
    },

    mounted() {
        this.metrics_chart = echarts.init(this.$refs.metrics_chart);
        this.session_chart = echarts.init(this.$refs.session_chart);
        this.chart_group = `dashboard_nat_map_${this._uid}`;
        this.metrics_chart.group = this.chart_group;
        this.session_chart.group = this.chart_group;
        echarts.connect(this.chart_group);
        this.metrics_chart.on("datazoom", this.remember_zoom);
        this.session_chart.on("datazoom", this.remember_zoom);
        window.addEventListener("resize", this.resize_chart);
        this.load_maps().then(() => {
            if (this.metrics_chart && this.selected_map !== null) {
                this.load_metrics();
            }
        });
    },

    beforeDestroy() {
        this.request_id += 1;
        window.removeEventListener("resize", this.resize_chart);
        if (this.chart_group) {
            echarts.disconnect(this.chart_group);
        }

        if (this.metrics_chart) {
            this.metrics_chart.off("datazoom", this.remember_zoom);
            this.metrics_chart.dispose();
            this.metrics_chart = null;
        }

        if (this.session_chart) {
            this.session_chart.off("datazoom", this.remember_zoom);
            this.session_chart.dispose();
            this.session_chart = null;
        }
    },

    methods: {
        change_map() {
            this.zoom_start = 0;
            this.zoom_end = 100;
            this.load_metrics();
        },

        change_range() {
            this.zoom_start = 0;
            this.zoom_end = 100;
            this.load_metrics();
        },

        change_resolution() {
            this.selected_range_ms = this.selected_interval.default_range_ms;
            this.zoom_start = 0;
            this.zoom_end = 100;
            this.load_metrics();
        },

        remember_zoom(event) {
            const data_zoom = event && event.batch ? event.batch[0] : event;
            if (data_zoom && Number.isFinite(data_zoom.start)) {
                this.zoom_start = data_zoom.start;
            }
            if (data_zoom && Number.isFinite(data_zoom.end)) {
                this.zoom_end = data_zoom.end;
            }
        },

        resize_chart() {
            if (this.metrics_chart) {
                this.metrics_chart.resize();
            }
            if (this.session_chart) {
                this.session_chart.resize();
            }
        },

        load_maps() {
            this.maps_loading = true;
            this.error_message = "";
            return api_client.run_get_query("/nat/maps/show")
            .then(response => {
                if (!this.metrics_chart) {
                    return;
                }

                const rows = parse_map_response(response.data);
                const keys = new Set();
                const maps = [];

                rows.forEach(row => {
                    if (
                        !row ||
                        !Number.isInteger(row.map_id) ||
                        row.map_id < 0 ||
                        !NAT_MAP_TYPES.includes(row.type)
                    ) {
                        throw new Error("NAT map list contains an invalid row");
                    }
                    const key = `${row.type}:${row.map_id}`;
                    if (keys.has(key)) {
                        throw new Error("NAT map list contains a duplicate row");
                    }
                    keys.add(key);
                    const map = {
                        map_id: row.map_id,
                        map_type: row.type,
                        value: key,
                    };
                    map.label = map_label(map);
                    maps.push(map);
                });

                maps.sort((left, right) => {
                    const type_difference = NAT_MAP_TYPES.indexOf(left.map_type) -
                        NAT_MAP_TYPES.indexOf(right.map_type);
                    return type_difference || left.map_id - right.map_id;
                });
                this.map_options = maps;

                if (!keys.has(this.selected_map_key)) {
                    this.selected_map_key = maps.length ? maps[0].value : null;
                }
            })
            .catch(error => {
                if (!this.metrics_chart) {
                    return;
                }

                this.error_message = request_error_message(error);
                this.map_options = [];
                this.selected_map_key = null;
                this.points_count = 0;
                this.session_points_count = 0;
                this.render_chart([]);
                this.render_chart([], "sessions");
            })
            .finally(() => {
                this.maps_loading = false;
            });
        },

        load_metrics() {
            const selected_map = this.selected_map;
            if (selected_map === null || !this.metrics_chart) {
                return Promise.resolve();
            }

            const end_time = new Date();
            const start_time = new Date(
                end_time.getTime() - this.selected_range_ms
            );
            const request_id = ++this.request_id;

            this.loading = true;
            this.error_message = "";
            this.session_error_message = "";
            const parameters = {
                start: start_time.toISOString(),
                end: end_time.toISOString(),
                map_id: String(selected_map.map_id),
                map_type: selected_map.map_type,
                resolution: this.selected_resolution,
            };
            const metrics_request = api_client.run_get_query(
                "/dashboard/nat_map_metrics", parameters,
            )
            .then(response => {
                if (request_id !== this.request_id) {
                    return;
                }
                const data = response.data || {};
                const points = Array.isArray(data.points) ? data.points : [];
                this.host = data.host || "";
                this.instance = data.instance || 1;
                this.points_count = points.length;
                this.render_chart(points);
            })
            .catch(error => {
                if (request_id !== this.request_id) {
                    return;
                }
                this.error_message = request_error_message(error);
                this.points_count = 0;
                this.render_chart([]);
            });

            const session_request = api_client.run_get_query(
                "/dashboard/nat_map_session_rates", parameters,
            )
            .then(response => {
                if (request_id !== this.request_id) {
                    return;
                }
                const data = response.data || {};
                const points = Array.isArray(data.points) ? data.points : [];
                this.session_points_count = points.length;
                this.render_chart(points, "sessions");
            })
            .catch(error => {
                if (request_id !== this.request_id) {
                    return;
                }
                this.session_error_message = request_error_message(error);
                this.session_points_count = 0;
                this.render_chart([], "sessions");
            });

            return Promise.all([metrics_request, session_request]).finally(() => {
                if (request_id === this.request_id) {
                    this.loading = false;
                }
            });
        },

        render_chart(points, kind = "states") {
            const session_rates = kind === "sessions";
            const chart = session_rates ? this.session_chart : this.metrics_chart;
            if (!chart) {
                return;
            }
            const definitions = session_rates ? SESSION_RATE_SERIES : METRIC_SERIES;
            const format_value = session_rates
                ? format_tooltip_session_rate : format_tooltip_count;
            const series = definitions.map(definition => ({
                name: definition.name,
                type: "line",
                data: points.map(point => [
                    point.time,
                    point[definition.field],
                ]),
                lineStyle: {
                    width: definition.width || 1.5,
                },
                sampling: "lttb",
                showSymbol: false,
            }));

            chart.setOption({
                animation: false,
                color: session_rates ? ["#2878b5", "#d62728"] : SERIES_COLORS,
                tooltip: {
                    trigger: "axis",
                    formatter: parameters => {
                        if (!parameters.length) {
                            return "";
                        }
                        const timestamp = new Date(
                            parameters[0].value[0]
                        ).toLocaleString();
                        const values = parameters.map(parameter => (
                            `${parameter.marker}${parameter.seriesName}: ` +
                            format_value(parameter.value[1])
                        ));
                        return [timestamp].concat(values).join("<br>");
                    },
                },
                legend: {
                    bottom: 48,
                    data: definitions.map(definition => definition.name),
                    height: session_rates ? 24 : 48,
                    left: 24,
                    right: 24,
                    type: "plain",
                },
                grid: {
                    bottom: session_rates ? 96 : 120,
                    containLabel: true,
                    left: 20,
                    right: 24,
                    top: 48,
                },
                xAxis: {
                    type: "time",
                    boundaryGap: false,
                    axisLabel: {
                        hideOverlap: true,
                    },
                },
                yAxis: {
                    type: "value",
                    min: 0,
                    name: session_rates ? "sessions/s" : "connections",
                    axisLabel: {
                        formatter: session_rates ? format_session_rate : format_axis_count,
                    },
                },
                dataZoom: [
                    {
                        id: "nat_map_inside_zoom",
                        type: "inside",
                        filterMode: "none",
                        start: this.zoom_start,
                        end: this.zoom_end,
                        xAxisIndex: 0,
                    },
                    {
                        id: "nat_map_slider_zoom",
                        type: "slider",
                        filterMode: "none",
                        start: this.zoom_start,
                        end: this.zoom_end,
                        xAxisIndex: 0,
                        bottom: 8,
                        height: 28,
                        realtime: true,
                        showDataShadow: true,
                        showDetail: true,
                        zoomLock: false,
                        left: 130,
                        right: 130,
                    },
                ],
                series,
            }, true);
        },
    },
};

</script>
